/** Persist File System Access API directory handles per sync profile (Chromium). */

const DB_NAME = 'hc-sync-dirs';
const STORE = 'handles';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveDirHandle(profileId: string, handle: FileSystemDirectoryHandle): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(handle, profileId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function loadDirHandle(profileId: string): Promise<FileSystemDirectoryHandle | null> {
  const db = await openDb();
  const handle = await new Promise<FileSystemDirectoryHandle | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(profileId);
    req.onsuccess = () => resolve((req.result as FileSystemDirectoryHandle) ?? null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return handle;
}

export async function pickLocalDirectory(): Promise<FileSystemDirectoryHandle | null> {
  if (typeof window.showDirectoryPicker !== 'function') {
    throw new Error('Folder pick is not supported in this browser (try Chrome or Edge).');
  }
  return window.showDirectoryPicker({ mode: 'readwrite' });
}

/** Skip OS junk and internal paths during sync (not user documents). */
export function shouldSkipSyncEntry(name: string, kind: 'file' | 'directory'): boolean {
  const lower = name.toLowerCase();
  if (name === '.hcloud_thumbs') return true;
  if (lower === '.ds_store' || lower === 'thumbs.db' || lower === 'desktop.ini') return true;
  if (lower === '.trash' || lower.endsWith('.trash')) return true;
  if (name.startsWith('._')) return true;
  if (kind === 'directory') {
    if (lower === '.trashes' || lower === '__macosx' || lower === '$recycle.bin') return true;
  }
  return false;
}

async function walkLocalDir(
  dir: FileSystemDirectoryHandle,
  prefix: string,
  out: Map<string, { handle: FileSystemFileHandle; size: number; modifiedMs: number }>
): Promise<void> {
  for await (const entry of dir.values()) {
    if (shouldSkipSyncEntry(entry.name, entry.kind === 'directory' ? 'directory' : 'file')) {
      continue;
    }
    const rel = prefix ? `${prefix}/${entry.name}` : entry.name;
    if (entry.kind === 'directory') {
      await walkLocalDir(entry as FileSystemDirectoryHandle, rel, out);
    } else if (entry.kind === 'file') {
      const fh = entry as FileSystemFileHandle;
      const file = await fh.getFile();
      out.set(rel.replace(/\\/g, '/'), {
        handle: fh,
        size: file.size,
        modifiedMs: file.lastModified,
      });
    }
  }
}

export async function listLocalFiles(
  dir: FileSystemDirectoryHandle
): Promise<Map<string, { handle: FileSystemFileHandle; size: number; modifiedMs: number }>> {
  const map = new Map<string, { handle: FileSystemFileHandle; size: number; modifiedMs: number }>();
  await walkLocalDir(dir, '', map);
  return map;
}

export async function writeLocalFile(
  root: FileSystemDirectoryHandle,
  relPath: string,
  blob: Blob
): Promise<void> {
  const parts = relPath.replace(/\\/g, '/').split('/').filter(Boolean);
  if (parts.length === 0) throw new Error('invalid path');
  let dir = root;
  for (let i = 0; i < parts.length - 1; i++) {
    dir = await dir.getDirectoryHandle(parts[i]!, { create: true });
  }
  const fh = await dir.getFileHandle(parts[parts.length - 1]!, { create: true });
  const w = await fh.createWritable();
  await w.write(blob);
  await w.close();
}
